import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import dayjs from 'dayjs';
import { eq } from 'ember-truth-helpers';
import { OTA_IDS } from 'frontend-utils/utils/constants/constant-values';
import { DOMAIN_NAME } from 'frontend-utils/utils/constants/domain-utils';
import generateOtaLink from 'frontend-utils/utils/url/generate-ota-link';
import GeneralRateIcons from 'lumen/components/general/rate-icons';
import type {
  CustomColumn,
  HotelRatesTablesCellsTdBaseSignature,
} from 'lumen/components/hotel/rates/tables/cells/td-base';
import HotelRatesTablesCellsTdBase from 'lumen/components/hotel/rates/tables/cells/td-base';
import HotelTooltipsTooltipRatesTable from 'lumen/components/hotel/tooltips/tooltip-rates-table';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type {
  requestParams,
  TableComparisonLayout,
} from 'lumen/utils/constants/rate-constants';
import { formatUrl } from 'lumen/utils/rates-request';

export interface TdRateCustomColumn extends CustomColumn {
  allowedOtaIds: string[];
  hotelinfoId: string;
  layout: TableComparisonLayout;
  requestParams: requestParams;
}

export interface HotelRatesTablesCellsTdRateSignature extends HotelRatesTablesCellsTdBaseSignature {
  Args: HotelRatesTablesCellsTdBaseSignature['Args'] & {
    column: TdRateCustomColumn;
  };
}

export default class HotelRatesTablesCellsTdRate<
  T extends HotelRatesTablesCellsTdRateSignature =
    HotelRatesTablesCellsTdRateSignature,
> extends HotelRatesTablesCellsTdBase<T> {
  @service declare access: Services['access'];
  @service declare ajax: AjaxService;
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotelinfoId() {
    return this.args.column.hotelinfoId;
  }

  get isAllChannels(): boolean {
    return this.otaId === OTA_IDS.ALL_CHANNELS;
  }

  get isNegative(): boolean {
    if (
      !this.rateObject ||
      !this.ownHotelRate ||
      this.ownHotelRate?.value === 0 ||
      this.rateObject?.value === 0 ||
      this.rateObject?.is_out_of_sync
    ) {
      return false;
    }

    return this.rateObject.value < this.ownHotelRate.value;
  }

  get isPastDate(): boolean {
    return !this.isToday && !this.isFuture;
  }

  get isPositive(): boolean {
    if (
      !this.rateObject ||
      !this.ownHotelRate ||
      this.ownHotelRate?.value === 0 ||
      this.rateObject?.value === 0 ||
      this.rateObject?.is_out_of_sync
    ) {
      return false;
    }

    return this.rateObject?.value > this.ownHotelRate?.value;
  }

  get isRateMissing(): boolean {
    return this.rateObject?.message === 'general.missing';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ownHotelRate() {
    if (this.period?.rates[this.hotelAndMarketService.hotel.hotelinfo.id!]) {
      return this.period?.rates?.[
        this.hotelAndMarketService.hotel.hotelinfo.id!
      ]?.[0];
    }

    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rateObject() {
    return this.period.rates[this.hotelinfoId]?.[0];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ratesTooltipValues() {
    if (this.isAllChannels) {
      const ratesForHotel = this.period.rates[this.hotelinfoId] ?? [];
      // Filter all rates for specific channel on which the current hotel does not have matchings
      return ratesForHotel.filter(
        (rate) => this.args.column.allowedOtaIds.indexOf(rate.ota) > -1,
      );
    }
    return this.rateObject;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rateValue() {
    if (!this.rateObject) {
      return null;
    }
    return this.hotelAndMarketService.formatRate(this.rateObject, {
      longFormat: false,
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get redirectUrl() {
    if (this.isPastDate || this.isRateMissing) {
      return null;
    }

    // Redirect to OTA of rate for ALL_CHANNEL, else take the selectedOTA (required for booking.com as brand.com)
    const redirectOta =
      this.otaId === OTA_IDS.ALL_CHANNELS ? this.rateObject?.ota : this.otaId;

    if (!redirectOta) {
      return null;
    }

    return generateOtaLink(
      redirectOta,
      this.hotelinfoId,
      this.hotelAndMarketService.hotel.id!,
      this.hotelAndMarketService.hotel?.isTripadvisorRebranding,
      this.period,
      this.maxPersons,
      'app_rates',
    );
  }

  // quick admin only way to check to full rate object of the cell
  @action
  onDoubleClick(): void {
    if (this.access.isStaffUser && console) {
      console.group(
        `Debugging info: ${this.args.column.name} - ${this.day?.date.format('ddd l')}`,
      );

      if (console.table) {
        console.table(this.rateObject);
      }

      // Show debugging links to the Bigtable viewer
      // Example URLs:
      // Fulls: /redirect/fulls?ota=branddotcom&from_date=2018-04-11&hotelinfo_id=38691&extract_date=2018-04-10
      // Scraped data: /redirect/scraped?ota=branddotcom&from_date=2018-04-11&hotelinfo_id=38691&extract_date=2018-04-10
      // Rates: /redirect/rates?ota=branddotcom&from_date=2018-04-11&hotelinfo_id=38691&extract_date=2018-04-10

      const strConsoleLayout = 'color: #005EA0; font-weight: bold;';

      const hotelId = this.hotelAndMarketService.hotel?.id;
      const selectedOTA = this.rateObject?.ota;

      console.log(
        `%cHotelInfo: ${this.ajax.host}/admin/hotel_industry/hotelinfo/${this.hotelinfoId}/change/`,
        strConsoleLayout,
      );

      if (this.period && this.rateObject?.extract_datetime) {
        const fromDate = this.period.id!.split(',')[0];

        // Conversion to local extract date happening here! WHY?
        // Newintegrities work with a local extract date timestamp, while the API still uses UTC extract dates.
        // We cannot simply use toLocalTime in hotelAndMarketService, because it rounds the UTC timestamp down T00:00:00, and then adds hours, which makes no change in some cases
        // So instead we take the UTC timestamp and preserve the hours, and we add the timezone to it in hours, the resulting day is the local extract date timestamp
        const extractDateUTC = dayjs.utc(this.rateObject?.extract_datetime);
        const timezone = this.hotelAndMarketService.timezone;
        const extractDateLocal = extractDateUTC

          .add(timezone, 'hour')
          .format('YYYY-MM-DD');
        const currentTab = 'rates';

        const scrapedDataURL = encodeURI(
          `${this.ajax.host}/redirect/scraped/?ota=${selectedOTA}&from_date=${fromDate}&hotelinfo_id=${this.hotelinfoId}&hotel_id=${hotelId}&extract_date=${extractDateLocal}&is_new_integrities=true&los=${this.los}&type=${currentTab}`,
        );
        const fullsURL = encodeURI(
          `${this.ajax.host}/redirect/fulls/?ota=${selectedOTA}&from_date=${fromDate}&hotelinfo_id=${this.hotelinfoId}&hotel_id=${hotelId}&extract_date=${extractDateLocal}&is_new_integrities=true&los=${this.los}&type=${currentTab}`,
        );
        const ratesURL = encodeURI(
          `${this.ajax.host}/redirect/rates/?ota=${selectedOTA}&from_date=${fromDate}&hotelinfo_id=${this.hotelinfoId}&hotel_id=${hotelId}&type=${currentTab}`,
        );

        const multiViewParams = {
          ota: selectedOTA,
          los: this.los,
          mealtype: this.mealPlan,
          persons: this.args.column.requestParams?.maxPersons,
          roomtype: this.roomType,
          bar: this.rateObject?.best_flex,
          membershiptype: this.rateObject?.membershiptype,
          from_date_range_start: fromDate,
          from_date_range_end: fromDate,
          hotel_id: hotelId,
          'hotelinfo_ids[]': this.hotelinfoId,
          meta: 'nested',
          type: currentTab,
          platform: this.args.column.requestParams?.platform,
          compset_ids: this.hotelAndMarketService.activeCompset?.apiId,
        };
        const multiViewURL = encodeURI(
          formatUrl(`https://viewer.${DOMAIN_NAME}/multiview`, multiViewParams),
        );

        console.log(`%cScraped data: ${scrapedDataURL}`, strConsoleLayout);
        console.log(`%cFulls: ${fullsURL}`, strConsoleLayout);
        console.log(`%cRates: ${ratesURL}`, strConsoleLayout);
        console.log(`%cMultiview: ${multiViewURL}`, strConsoleLayout);

        const taxConfigParams = {
          subscription_id: hotelId,
          currency: this.rateObject?.currency,
          shop_currency: this.rateObject?.shop_currency,
          value: this.rateObject?.value,
          shop_value: this.rateObject?.shop_value,
          vat: this.rateObject?.vat,
          vat_incl: this.rateObject?.vat_incl,
          city_tax: this.rateObject?.city_tax,
          city_tax_incl: this.rateObject?.city_tax_incl,
          other_taxes: this.rateObject?.other_taxes,
          other_taxes_incl: this.rateObject?.other_taxes_incl,
          shop_vat_incl: this.rateObject?.shop_vat_incl,
          shop_other_taxes_incl: this.rateObject?.shop_other_taxes_incl,
          shop_city_tax_incl: this.rateObject?.shop_city_tax_incl,
          room_name: this.rateObject?.room_name,
          cancellation: this.rateObject?.best_flex, // Should be updated to bar in the future (see OTA-7604)
          extract_datetime: this.rateObject?.extract_datetime,
          max_persons: this.rateObject?.max_persons,
          mealtype_included: this.rateObject?.mealtype_included,
          membershiptype: this.rateObject?.membershiptype,
          ota: this.rateObject?.ota,
          hotelinfo: this.hotelinfoId,
          type: currentTab,
          live_url: this.redirectUrl
            ? encodeURIComponent(this.redirectUrl)
            : null,
        };
        const taxConfigUri = encodeURI(
          formatUrl(
            '/admin/matching/taxconfig/taxconfig_calculation/',
            taxConfigParams,
            this.ajax.host,
          ),
        );

        let taxConfigMessage = taxConfigUri;
        const oneHourAgo = dayjs.utc().subtract(1, 'hour');
        if (extractDateUTC && extractDateUTC.isBefore(oneHourAgo)) {
          taxConfigMessage = 'only available after performing a live shop';
        }

        console.log(`%cTax configs: ${taxConfigMessage}`, strConsoleLayout);
      } else {
        console.log('value has no period or extract_datetime');
      }

      console.groupEnd();
    }
  }

  <template>
    {{! template-lint-disable no-nested-interactive }}
    <div
      class="{{if
          (eq @column.textAlign 'left')
          'flex items-center justify-start'
          'flex items-center justify-end'
        }}
        text-200--regular truncate tabular-nums
        {{if this.isPositive 'text-success-default'}}
        {{if this.isNegative 'text-critical-default'}}
        {{if this.isPast 'td-past'}}
        {{if this.isToday 'td-today'}}"
      data-test-id="rate-cell"
      role="tooltip"
      {{on "dblclick" this.onDoubleClick}}
    >
      {{#if this.redirectUrl}}
        <a
          class="underline-on-hover truncate"
          href={{this.redirectUrl}}
          rel="noopener noreferrer"
          target="_blank"
        >
          <p class="truncate">{{this.rateValue}}</p>
        </a>
      {{else}}
        <p class="truncate">{{this.rateValue}}</p>
      {{/if}}

      <GeneralRateIcons
        @leaveSpaceWhenEmpty={{true}}
        @mealPlan={{this.mealPlan}}
        @otaId={{this.otaId}}
        @rate={{this.rateObject}}
        @rateParam={{this.rateParam}}
        @size="small"
      />
    </div>

    {{#unless this.isRateMissing}}
      <HotelTooltipsTooltipRatesTable
        @date={{this.day.date}}
        @fetchOtherLosRates={{true}}
        @hotel={{this.hotelAndMarketService.hotel}}
        @isAllChannels={{this.isAllChannels}}
        @ownHotelRate={{this.ownHotelRate}}
        @requestParams={{@column.requestParams}}
        @value={{this.ratesTooltipValues}}
      />
    {{/unless}}
  </template>
}
